import React, { useCallback, useEffect, useState } from 'react';
import { LifeBuoy } from 'lucide-react';
import { TYPE, useTheme } from '../theme';
import { Button, Card, EmptyState, ErrorState, Input, Loader, Segmented } from '../components/ui';
import { modAdminApi, type SupportTicket } from '../components/moderation/moderationApi';

// AS1c · Admin → Support: ärenden från kontaktsidan (/support och Profil). Svar skickas som mejl
// till avsändaren; här markeras ärendet som klart.

const fmt = (ms: number) => new Date(ms).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' });

export function SupportTickets() {
  const T = useTheme();
  const [status, setStatus] = useState<'open' | 'closed'>('open');
  const [tickets, setTickets] = useState<SupportTicket[] | null>(null);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = useCallback(() => {
    setError('');
    modAdminApi.tickets(status).then(setTickets).catch(e => { setError(e.message); setTickets([]); });
  }, [status]);
  useEffect(() => { setTickets(null); load(); }, [load]);

  const close = (t: SupportTicket) => modAdminApi.closeTicket(t.id, notes[t.id] ?? '').then(load).catch(e => alert(e.message));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <p style={{ ...TYPE.small, color: T.textSub, margin: 0, flex: '1 1 260px' }}>Ärenden från kontaktformuläret. Svara med mejl till avsändaren och markera ärendet som klart.</p>
        <Segmented size="sm" block={false} value={status} onChange={setStatus} label="Visa"
          options={[{ value: 'open', label: 'Öppna' }, { value: 'closed', label: 'Klara' }]} />
      </div>
      {error && <ErrorState message={error} />}
      {tickets === null ? <Loader text="Hämtar ärenden…" /> : tickets.length === 0 && !error ? (
        <Card><EmptyState icon={LifeBuoy} title={status === 'open' ? 'Inga öppna ärenden' : 'Inga klara ärenden än'} /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {tickets.map(t => (
            <Card key={t.id} padding={16}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ color: T.text, fontWeight: 700, flex: '1 1 200px', minWidth: 0, overflowWrap: 'anywhere' }}>{t.subject}</span>
                <span style={{ ...TYPE.small, color: T.textMuted }}>{fmt(t.createdAt)}</span>
              </div>
              <div style={{ ...TYPE.small, color: T.textSub, marginBottom: 10, overflowWrap: 'anywhere' }}>
                {t.name ? `${t.name} · ` : ''}<a href={`mailto:${t.email}?subject=${encodeURIComponent(`Sv: ${t.subject}`)}`} style={{ color: T.accentText, fontWeight: 600 }}>{t.email}</a>
                {t.userEmail ? ' · inloggad' : ''}
              </div>
              <div style={{ color: T.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: 15 }}>{t.message}</div>
              {t.status === 'open' ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                  <Input aria-label="Anteckning" placeholder="Anteckning (valfritt)" value={notes[t.id] ?? ''} onChange={e => setNotes(n => ({ ...n, [t.id]: e.target.value }))} style={{ flex: '1 1 200px' }} />
                  <Button size="sm" onClick={() => close(t)}>Markera som klart</Button>
                </div>
              ) : (
                <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 10 }}>Klart {t.closedAt ? fmt(t.closedAt) : ''} · {t.closedBy}{t.adminNote ? ` – ${t.adminNote}` : ''}</div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
